import { useEffect, useState } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { selectedDefinedTerm } from "../domain/definedTerms";
import { docToPlainText, richTextParseErrorMessage, type RichTextDoc } from "../domain/richText";
import {
  emptyImageInsertDraftState,
  httpUrlParseErrorMessage,
  imageInsertDraftCommand,
  nextImageInsertDraftState,
} from "../domain/url";
import { jsonContentToRichTextDocResult, richTextDocToJsonContent, tiptapContentNeedsSync } from "../lib/tiptapRichText";

/**
 * TipTap rich-text editor for a section's content. Emits the document as
 * TipTap JSON on every change. Mount with a `key={sectionId}` so switching
 * sections re-initialises the editor with fresh content.
 */
export function SectionEditor({
  value,
  onChange,
  onDefineTerm,
  placeholder = "Start typing the section text here. Use Save as amendment when this change should create update markup.",
}: {
  value: RichTextDoc;
  onChange: (doc: RichTextDoc) => void;
  onDefineTerm?: (term: string) => void;
  placeholder?: string;
}) {
  const [imageDraft, setImageDraft] = useState(() => emptyImageInsertDraftState());
  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Image,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: richTextDocToJsonContent(value),
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: "min-h-[12rem] rounded-b border border-slate-300 bg-white p-3 text-sm leading-relaxed text-slate-800 focus:outline-none",
      },
    },
    onUpdate: ({ editor }) => {
      const parsed = jsonContentToRichTextDocResult(editor.getJSON());
      if (parsed.ok) {
        onChange(parsed.value);
        return;
      }
      console.warn(richTextParseErrorMessage(parsed.error));
    },
  });

  useEffect(() => {
    if (editor === null) return;
    if (tiptapContentNeedsSync(editor.getJSON(), value)) {
      editor.commands.setContent(richTextDocToJsonContent(value), false);
    }
  }, [editor, value]);

  if (editor === null) return <div className="text-sm text-slate-400">Loading editor…</div>;

  const isEmpty = docToPlainText(value).trim() === "";

  const selectedText = (): string => {
    const { from, to } = editor.state.selection;
    return editor.state.doc.textBetween(from, to, " ");
  };

  const submitImageDraft = () => {
    const command = imageInsertDraftCommand(imageDraft);
    if (command.kind === "invalid") {
      setImageDraft((current) => nextImageInsertDraftState(current, { type: "failed", error: command.error }));
      return;
    }
    if (command.kind !== "ready") return;
    editor.chain().focus().setImage({ src: command.src }).run();
    setImageDraft((current) => nextImageInsertDraftState(current, { type: "inserted" }));
  };

  const imageDraftError = imageDraft.kind === "editing" && imageDraft.error !== null
    ? httpUrlParseErrorMessage(imageDraft.error)
    : null;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 rounded-t border border-b-0 border-slate-300 bg-slate-50 p-1">
        <ToolbarButton editor={editor} mark="bold" label="B" title="Bold" className="font-bold" />
        <ToolbarButton editor={editor} mark="italic" label="I" title="Italic" className="italic" />
        <ToolbarButton editor={editor} mark="strike" label="S" title="Strikethrough" className="line-through" />
        <Divider />
        <button type="button" title="Heading" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btn(editor.isActive("heading", { level: 2 }))}>
          H
        </button>
        <button type="button" title="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive("bulletList"))}>
          •
        </button>
        <button type="button" title="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive("orderedList"))}>
          1.
        </button>
        <Divider />
        <button
          type="button"
          title="Insert table"
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          className={btn(editor.isActive("table"))}
        >
          ▦
        </button>
        <button
          type="button"
          title="Insert image by URL"
          onClick={() => setImageDraft((current) => nextImageInsertDraftState(current, { type: "opened" }))}
          className={btn(imageDraft.kind === "editing")}
        >
          ▣
        </button>
        {imageDraft.kind === "editing" && (
          <form
            className="flex min-w-64 flex-wrap items-center gap-1"
            onSubmit={(event) => {
              event.preventDefault();
              submitImageDraft();
            }}
          >
            <input
              aria-label="Image URL"
              className="min-w-56 rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 focus:border-brand focus:outline-none"
              value={imageDraft.value}
              onChange={(event) => setImageDraft((current) => nextImageInsertDraftState(current, { type: "valueInput", value: event.target.value }))}
              placeholder="https://..."
            />
            <button type="submit" className={btn(false)}>Add</button>
            <button
              type="button"
              className={btn(false)}
              onClick={() => setImageDraft((current) => nextImageInsertDraftState(current, { type: "cancelled" }))}
            >
              Cancel
            </button>
            {imageDraftError !== null && <span className="basis-full text-[11px] text-red-600">{imageDraftError}</span>}
          </form>
        )}
        {onDefineTerm !== undefined && (
          <>
            <Divider />
            <button
              type="button"
              title="Define the selected term"
              onClick={() => {
                const term = selectedDefinedTerm(selectedText());
                if (term.ok) onDefineTerm(term.value);
              }}
              className="rounded px-2 py-1 text-xs font-medium text-brand hover:bg-brand-light"
            >
              Define term
            </button>
          </>
        )}
      </div>
      <div className="relative">
        <EditorContent editor={editor} />
        {isEmpty && (
          <div className="pointer-events-none absolute left-3 top-3 text-sm text-slate-400">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolbarButton({
  editor,
  mark,
  label,
  title,
  className = "",
}: {
  editor: Editor;
  mark: "bold" | "italic" | "strike";
  label: string;
  title: string;
  className?: string;
}) {
  const toggle = () => {
    const chain = editor.chain().focus();
    if (mark === "bold") chain.toggleBold().run();
    else if (mark === "italic") chain.toggleItalic().run();
    else chain.toggleStrike().run();
  };
  return (
    <button type="button" title={title} onClick={toggle} className={`${btn(editor.isActive(mark))} ${className}`}>
      {label}
    </button>
  );
}

const btn = (active: boolean): string =>
  `rounded px-2 py-1 text-xs ${active ? "bg-brand text-white" : "text-slate-700 hover:bg-slate-200"}`;

const Divider = () => <span className="mx-1 h-5 w-px bg-slate-300" />;
